/**
 * @noble 封装层 —— 全站唯一的曲线 / 哈希入口。
 *
 * 选型理由（ARCH §S8）：纯 JS，规避 Chrome 133+/Safari 17+ 的 WebCrypto
 * X25519/Ed25519 门槛，跨浏览器行为一致、可审计。
 */

import { ed25519, x25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha256';
import { sha512 } from '@noble/hashes/sha512';
import { hmac } from '@noble/hashes/hmac';
import { hkdf } from '@noble/hashes/hkdf';
import { randomBytes as nobleRandomBytes } from '@noble/hashes/utils';

export const ED25519_PRIV_LEN = 32;
export const ED25519_PUB_LEN = 32;
export const ED25519_SIG_LEN = 64;

/** 安全随机字节（CSPRNG）。 */
export function randomBytes(length: number): Uint8Array {
  return nobleRandomBytes(length);
}

/** 安全清零（尽最大努力，JS 无法保证上游无拷贝）。 */
export function zeroBytes(bytes: Uint8Array): void {
  if (!bytes) return;
  bytes.fill(0);
}

// ───────────────────────── 哈希 ─────────────────────────

export function hashSha256(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const merged = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    merged.set(p, offset);
    offset += p.length;
  }
  return sha256(merged);
}

export function hashSha512(bytes: Uint8Array): Uint8Array {
  return sha512(bytes);
}

/** HMAC-SHA512（分域派生用）。 */
export function hmacSha512(key: Uint8Array, data: Uint8Array): Uint8Array {
  return hmac(sha512, key, data);
}

/** HMAC-SHA256（会话派生用）。 */
export function hmacSha256(key: Uint8Array, data: Uint8Array): Uint8Array {
  return hmac(sha256, key, data);
}

/** HKDF-SHA256 → 定长输出。 */
export function hkdfSha256(ikm: Uint8Array, salt: Uint8Array, info: Uint8Array, length = 32): Uint8Array {
  return hkdf(sha256, ikm, salt, info, length);
}

// ───────────────────────── Ed25519 ─────────────────────────

/** 种子 → Ed25519 公钥（32B）。 */
export function ed25519PublicKey(seed: Uint8Array): Uint8Array {
  return ed25519.getPublicKey(seed);
}

/** Ed25519 签名（64B）。 */
export function ed25519Sign(message: Uint8Array, seed: Uint8Array): Uint8Array {
  return ed25519.sign(message, seed);
}

/** Ed25519 验签。任何异常都回落 false，绝不抛给 UI。 */
export function ed25519Verify(signature: Uint8Array, message: Uint8Array, publicKey: Uint8Array): boolean {
  try {
    return ed25519.verify(signature, message, publicKey);
  } catch {
    return false;
  }
}

// ───────────────────────── X25519 ─────────────────────────

/** 种子 → X25519 公钥（32B）。 */
export function x25519PublicKey(seed: Uint8Array): Uint8Array {
  return x25519.getPublicKey(seed);
}

/** X25519 ECDH 共享密钥（32B）。失败返回 null。 */
export function x25519SharedSecret(privateSeed: Uint8Array, peerPublicKey: Uint8Array): Uint8Array | null {
  try {
    return x25519.getSharedSecret(privateSeed, peerPublicKey);
  } catch {
    return null;
  }
}

// ───────────────────────── base58btc（PeerID 用） ─────────────────────────

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

/** Uint8Array → base58btc（无 multibase 前缀，libp2p PeerID 形态）。 */
export function base58Encode(bytes: Uint8Array): string {
  if (bytes.length === 0) return '';
  const digits: number[] = [0];
  for (const byte of bytes) {
    let carry = byte;
    for (let i = 0; i < digits.length; i += 1) {
      carry += digits[i] << 8;
      digits[i] = carry % 58;
      carry = (carry / 58) | 0;
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = (carry / 58) | 0;
    }
  }
  let out = '';
  // 前导 0 字节 → '1'
  for (const byte of bytes) {
    if (byte === 0) out += BASE58_ALPHABET[0];
    else break;
  }
  for (let i = digits.length - 1; i >= 0; i -= 1) out += BASE58_ALPHABET[digits[i]];
  return out;
}

/** base58btc → Uint8Array。非法字符返回 null。 */
export function base58Decode(value: string): Uint8Array | null {
  if (!value) return null;
  const bytes: number[] = [0];
  for (const ch of value) {
    const index = BASE58_ALPHABET.indexOf(ch);
    if (index < 0) return null;
    let carry = index;
    for (let i = 0; i < bytes.length; i += 1) {
      carry += bytes[i] * 58;
      bytes[i] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  let leadingZeros = 0;
  for (const ch of value) {
    if (ch === BASE58_ALPHABET[0]) leadingZeros += 1;
    else break;
  }
  const decoded = new Uint8Array(leadingZeros + bytes.length);
  for (let i = 0; i < bytes.length; i += 1) decoded[leadingZeros + i] = bytes[bytes.length - 1 - i];
  return decoded;
}
